import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, Printer, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { accountService, type ReceiptData } from '../../services/account.service';

const fmtIDR = (n: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

/**
 * Printable receipt for one payment.
 *
 * Rendered outside the account sidebar shell so `window.print()` produces a clean
 * document; `print:hidden` strips the navigation chrome. No PDF library — the
 * browser's own "Save as PDF" is what people actually use.
 */
export const ReceiptView: React.FC = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const [data, setData] = React.useState<ReceiptData | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        void (async () => {
            if (!orderId) return;
            const res = await accountService.getReceipt(orderId);
            if (res.success && res.data) setData(res.data);
            else setError((res as any).message || 'Kwitansi tidak ditemukan');
            setLoading(false);
        })();
    }, [orderId]);

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="mx-auto max-w-lg px-6 py-16 text-center">
                <AlertCircle className="mx-auto h-8 w-8 text-amber-500" />
                <p className="mt-3 text-gray-700">{error}</p>
                <Link to="/account/billing" className="mt-4 inline-block text-sm text-indigo-600 hover:underline">
                    ← Kembali ke Billing
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-10 print:bg-white print:py-0">
            <div className="mx-auto max-w-2xl px-6">
                <div className="mb-4 flex items-center justify-between print:hidden">
                    <Link
                        to="/account/billing"
                        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft className="h-4 w-4" /> Billing
                    </Link>
                    <Button variant="outline" onClick={() => window.print()}>
                        <Printer className="mr-2 h-4 w-4" /> Cetak / Simpan PDF
                    </Button>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-8 print:border-0 print:p-0">
                    <div className="flex items-start justify-between border-b border-gray-200 pb-6">
                        <div>
                            <p className="text-xl font-semibold text-gray-900">HyVay</p>
                            <p className="text-sm text-gray-500">Kwitansi pembayaran</p>
                        </div>
                        <div className="text-right text-sm">
                            <p className="font-mono text-gray-900">{data.orderId}</p>
                            <p className="text-gray-500">
                                {new Date(data.createdAt).toLocaleString('id-ID', {
                                    dateStyle: 'long',
                                    timeStyle: 'short',
                                })}
                            </p>
                            <span
                                className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                    data.status === 'SUCCESS'
                                        ? 'bg-green-100 text-green-700'
                                        : data.status === 'FAILED'
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-yellow-100 text-yellow-800'
                                }`}
                            >
                                {data.status}
                            </span>
                        </div>
                    </div>

                    <div className="grid gap-6 py-6 sm:grid-cols-2">
                        <div>
                            <p className="text-xs uppercase tracking-wider text-gray-400">Ditagihkan kepada</p>
                            <p className="mt-1 text-sm text-gray-900">{data.customer.name || '—'}</p>
                            <p className="text-sm text-gray-500">{data.customer.email}</p>
                            {data.customer.company && <p className="text-sm text-gray-500">{data.customer.company}</p>}
                        </div>
                    </div>

                    <table className="w-full border-t border-gray-200 text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-gray-400">
                                <th className="py-3 font-medium">Deskripsi</th>
                                <th className="py-3 text-right font-medium">Jumlah</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-t border-gray-100">
                                <td className="py-3 text-gray-900">
                                    Topup HyVay Credit
                                    {data.credits ? (
                                        <span className="block text-xs text-gray-500">
                                            {data.credits.toLocaleString('id-ID', { maximumFractionDigits: 3 })} credits
                                        </span>
                                    ) : null}
                                </td>
                                <td className="py-3 text-right tabular-nums text-gray-900">{fmtIDR(data.amount)}</td>
                            </tr>
                            <tr className="border-t border-gray-200 font-semibold">
                                <td className="py-3 text-gray-900">Total</td>
                                <td className="py-3 text-right tabular-nums text-gray-900">{fmtIDR(data.amount)}</td>
                            </tr>
                        </tbody>
                    </table>

                    <p className="mt-6 border-t border-gray-200 pt-4 text-xs leading-relaxed text-gray-400">
                        HyVay Credit bukan uang tunai dan tidak dapat dikembalikan atau ditarik setelah ditambahkan ke
                        akun. Kwitansi ini dibuat otomatis oleh sistem.
                    </p>
                </div>
            </div>
        </div>
    );
};
